import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  Scale,
  RefreshCw,
  Play,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  KeyRound,
  X,
  ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ChipBankTabs from '../components/ChipBankTabs';

// ----------------------------------------------------------------
// Reconciliation — Phase 1.7 admin page
// ----------------------------------------------------------------
// Shows the most recent runs of the daily ledger-integrity check, plus
// a "Run now" button that triggers an ad-hoc run with a supervisor PIN.
//
// A healthy run shows zero anomalies and matches the cached liability.
// Any anomaly count > 0 needs immediate investigation.

const fmtCents = (cents) => {
  if (cents == null) return '—';
  return (Number(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const fmtDuration = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
};

const ANOMALY_LABEL = {
  balance_drift: 'Balance drift',
  hash_chain_break: 'Hash chain break',
  hash_mismatch: 'Hash mismatch',
  orphan_entry: 'Orphan entry',
};

const ANOMALY_DESCRIPTION = {
  balance_drift:
    "The wallet's cached balance doesn't match the algebraic sum of its ledger entries.",
  hash_chain_break:
    "An entry's prev_hash doesn't match the prior entry's entry_hash for that wallet.",
  hash_mismatch:
    "An entry's stored entry_hash doesn't match the value recomputed from its canonical payload.",
  orphan_entry:
    'A ledger entry references a wallet or session that no longer exists.',
};

export default function Reconciliation() {
  const [employeeNumber, setEmployeeNumberState] = useState(
    () =>
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem('cage.employeeNumber')) ||
      ''
  );
  const setEmployeeNumber = (v) => {
    setEmployeeNumberState(v);
    if (typeof localStorage !== 'undefined') {
      if (v) localStorage.setItem('cage.employeeNumber', v);
      else localStorage.removeItem('cage.employeeNumber');
    }
  };

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pinModal, setPinModal] = useState(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/cage/reconciliation/runs', {
        params: { limit: 25 },
      });
      setRuns(res.data.runs);
      if (!selectedId && res.data.runs.length > 0) {
        setSelectedId(res.data.runs[0].id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/cage/reconciliation/runs/${id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load run');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  const triggerRun = () => {
    setPinModal({
      title: 'Run reconciliation',
      description:
        'Performs a full integrity pass over every wallet and ledger entry. Read-only — no balances are modified. Requires a supervisor cage PIN.',
      submitLabel: 'Run now',
      action: async ({ pin, employeeNumber: empNum }) => {
        const res = await api.post('/admin/cage/reconciliation/run', {
          // Use the value passed from the modal at submit time, NOT the closure
          // captured when the modal opened (which is stale/empty for first-time
          // users who type their number into the modal).
          employeeNumber: empNum,
          staffPin: pin,
        });
        toast.success(
          `Run complete — ${res.data.run.anomalyCount} anomalies, ` +
            `${res.data.run.walletsChecked} wallets`
        );
        await fetchRuns();
        setSelectedId(res.data.run.id);
      },
    });
  };

  // Latest healthy summary card on top
  const latestComplete = runs.find((r) => r.status === 'completed');
  const totalLiability = latestComplete?.totalLiabilityCents;

  return (
    <div className="p-6 space-y-6">
      <ChipBankTabs />
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <Scale className="text-amber-500" size={26} />
            Ledger Reconciliation
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Daily integrity check (4:30 AM PT). Verifies balances, hash chain, and entry hashes.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchRuns}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-gray-200"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={triggerRun}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg"
          >
            <Play size={16} />
            Run now
          </button>
        </div>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          label="Total player bank liability"
          value={totalLiability != null ? fmtCents(totalLiability) : '—'}
          sub={
            latestComplete
              ? `as of ${fmtTime(latestComplete.startedAt)}`
              : 'no completed runs yet'
          }
          accent="amber"
        />
        <Card
          label="Last run"
          value={
            latestComplete?.anomalyCount === 0 ? (
              <span className="flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400" /> Clean
              </span>
            ) : latestComplete ? (
              <span className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-red-400" />
                {latestComplete.anomalyCount} anomalies
              </span>
            ) : (
              '—'
            )
          }
          sub={
            latestComplete
              ? `${latestComplete.walletsChecked} wallets · ${latestComplete.entriesChecked} entries`
              : ''
          }
          accent={latestComplete?.anomalyCount === 0 ? 'green' : 'red'}
        />
        <Card
          label="Recent runs"
          value={runs.length}
          sub={runs.filter((r) => r.status === 'failed').length + ' failed'}
          accent="gray"
        />
      </div>

      {/* Two-column: list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List of recent runs */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-white font-medium">Recent runs</h2>
            {loading && (
              <RefreshCw size={14} className="animate-spin text-amber-500" />
            )}
          </div>
          {!loading && runs.length === 0 && (
            <div className="p-8 text-center text-gray-400">
              <Scale size={36} className="mx-auto mb-2 opacity-30" />
              <div>No runs yet. Click "Run now" to perform the first.</div>
            </div>
          )}
          <ul className="divide-y divide-gray-700/50 max-h-[70vh] overflow-y-auto">
            {runs.map((r) => (
              <RunRow
                key={r.id}
                run={r}
                selected={r.id === selectedId}
                onClick={() => setSelectedId(r.id)}
              />
            ))}
          </ul>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 bg-gray-800 border border-gray-700 rounded-xl min-h-[500px]">
          {detailLoading && (
            <div className="p-8 text-center text-gray-400">
              <RefreshCw className="mx-auto animate-spin text-amber-500" />
            </div>
          )}
          {!detailLoading && !detail && (
            <div className="p-8 text-center text-gray-400">
              <Scale size={36} className="mx-auto mb-2 opacity-30" />
              <div>Select a run to inspect anomalies.</div>
            </div>
          )}
          {!detailLoading && detail && <RunDetail detail={detail} />}
        </div>
      </div>

      {pinModal && (
        <PinModal
          modal={pinModal}
          employeeNumber={employeeNumber}
          setEmployeeNumber={setEmployeeNumber}
          onClose={() => setPinModal(null)}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------
function Card({ label, value, sub, accent }) {
  const accents = {
    amber: 'text-amber-400',
    red: 'text-red-400',
    green: 'text-green-400',
    gray: 'text-gray-300',
  };
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${accents[accent] || ''}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function StatusPill({ status, anomalyCount }) {
  if (status === 'failed') {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium border bg-red-900/40 border-red-700/50 text-red-200">
        Failed
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium border bg-blue-900/40 border-blue-700/50 text-blue-200 flex items-center gap-1 w-fit">
        <RefreshCw size={10} className="animate-spin" /> Running
      </span>
    );
  }
  if (anomalyCount > 0) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium border bg-red-900/40 border-red-700/50 text-red-200">
        {anomalyCount} anomalies
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded text-xs font-medium border bg-green-900/40 border-green-700/50 text-green-200">
      Clean
    </span>
  );
}

function RunRow({ run, selected, onClick }) {
  return (
    <li
      onClick={onClick}
      className={`px-4 py-3 cursor-pointer flex items-center gap-3 ${
        selected ? 'bg-amber-900/20 border-l-4 border-amber-500' : 'hover:bg-gray-700/40'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <StatusPill status={run.status} anomalyCount={run.anomalyCount} />
          <span className="text-xs text-gray-400">{run.triggerType}</span>
        </div>
        <div className="text-sm text-white">{fmtTime(run.startedAt)}</div>
        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
          <Clock size={11} />
          {fmtDuration(run.durationMs)}
          <span className="text-gray-600">·</span>
          <span>
            {run.walletsChecked}w / {run.entriesChecked}e
          </span>
        </div>
      </div>
      <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
    </li>
  );
}

function RunDetail({ detail }) {
  const r = detail.run;
  const anomalies = detail.anomalies || [];
  const grouped = anomalies.reduce((acc, a) => {
    (acc[a.anomalyType] = acc[a.anomalyType] || []).push(a);
    return acc;
  }, {});

  return (
    <div className="p-5 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            Run {r.id.slice(0, 8)}
            <StatusPill status={r.status} anomalyCount={r.anomalyCount} />
          </h3>
          <div className="text-sm text-gray-400 mt-1">
            {r.triggerType === 'cron' ? 'Scheduled' : 'Manual'}
            {r.triggeredBy && ` · by ${r.triggeredBy}`} · {fmtTime(r.startedAt)}
          </div>
        </div>
      </div>

      {r.errorMessage && (
        <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-3 flex items-start gap-2">
          <XCircle size={18} className="text-red-400 mt-0.5" />
          <div>
            <div className="text-red-200 font-medium text-sm">Run failed</div>
            <div className="text-red-300/80 text-xs mt-1">{r.errorMessage}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <DetailField label="Wallets checked" value={r.walletsChecked} />
        <DetailField label="Entries checked" value={r.entriesChecked} />
        <DetailField label="Anomalies" value={r.anomalyCount} accent={r.anomalyCount > 0 ? 'red' : 'green'} />
        <DetailField
          label="Liability"
          value={fmtCents(r.totalLiabilityCents)}
        />
      </div>

      {anomalies.length === 0 ? (
        <div className="bg-green-900/10 border border-green-700/30 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle size={22} className="text-green-400 mt-0.5" />
          <div>
            <div className="text-green-200 font-medium">No anomalies detected</div>
            <div className="text-green-300/70 text-sm mt-0.5">
              Cached balances match the ledger sum, the hash chain is intact, and
              every entry's hash recomputes correctly.
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([type, list]) => (
            <AnomalyGroup key={type} type={type} anomalies={list} />
          ))}
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value, accent }) {
  const accents = {
    red: 'text-red-400',
    green: 'text-green-400',
  };
  return (
    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-lg font-semibold mt-1 text-white ${accents[accent] || ''}`}>
        {value}
      </div>
    </div>
  );
}

function AnomalyGroup({ type, anomalies }) {
  return (
    <div className="bg-red-900/10 border border-red-700/30 rounded-lg overflow-hidden">
      <div className="px-4 py-2 bg-red-900/20 border-b border-red-700/30">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-400" />
          <div>
            <div className="text-red-200 font-medium text-sm">
              {ANOMALY_LABEL[type] || type} ({anomalies.length})
            </div>
            <div className="text-xs text-red-300/70">{ANOMALY_DESCRIPTION[type]}</div>
          </div>
        </div>
      </div>
      <ul className="divide-y divide-red-700/30">
        {anomalies.map((a) => (
          <li key={a.id} className="px-4 py-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-white">
                {a.wallet?.user?.name || (a.wallet ? a.wallet.id.slice(0, 8) : 'no wallet')}
              </span>
              {a.wallet?.user?.email && (
                <span className="text-xs text-gray-400">{a.wallet.user.email}</span>
              )}
            </div>
            {(a.expectedValue || a.actualValue) && (
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <div>
                  <div className="text-gray-500">Expected</div>
                  <div className="text-green-300 font-mono">{a.expectedValue || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Actual</div>
                  <div className="text-red-300 font-mono">{a.actualValue || '—'}</div>
                </div>
              </div>
            )}
            {a.ledgerEntryId && (
              <div className="text-xs text-gray-500 mt-1 font-mono">
                entry {a.ledgerEntryId.slice(0, 8)}…
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// PIN modal — minimal version. Reuses the supervisor PIN pattern.
function PinModal({ modal, employeeNumber, setEmployeeNumber, onClose }) {
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!employeeNumber) return toast.error('Enter your employee number');
    if (!pin) return toast.error('PIN required');
    setSubmitting(true);
    try {
      await modal.action({ pin, employeeNumber });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{modal.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        {modal.description && (
          <p className="text-gray-400 text-sm">{modal.description}</p>
        )}
        {!employeeNumber && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Employee #
            </label>
            <input
              type="text"
              value={employeeNumber}
              onChange={(e) => setEmployeeNumber(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              autoFocus
            />
          </div>
        )}
        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            <KeyRound size={12} className="inline mr-1" />
            Supervisor PIN
          </label>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            placeholder="••••"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50"
          >
            {submitting ? (
              <RefreshCw className="animate-spin" size={16} />
            ) : (
              <Play size={16} />
            )}
            {modal.submitLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
